import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, probeOwner } from "../lib/api";
import type { Shader } from "../types";
import { MastHead } from "../components/MastHead";
import { FilterBar } from "../components/FilterBar";
import { EntryCard } from "../components/EntryCard";
import { Detail } from "../components/Detail";

export function Gallery() {
  const [shaders, setShaders] = useState<Shader[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<Shader | null>(null);
  const [tag, setTag] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [ownerMode, setOwnerMode] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .list()
      .then((data) => {
        if (!alive) return;
        setShaders(data);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e.message);
        setLoading(false);
      });
    probeOwner().then((email) => alive && setOwnerMode(!!email));
    return () => {
      alive = false;
    };
  }, []);

  const tags = useMemo(() => {
    const s = new Set<string>();
    shaders.forEach((e) => e.tags.forEach((t) => s.add(t)));
    return [...s];
  }, [shaders]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return shaders.filter((e) => {
      if (tag && !e.tags.includes(tag)) return false;
      if (!qq) return true;
      return (
        e.title.toLowerCase().includes(qq) ||
        e.tags.some((t) => t.includes(qq)) ||
        ("w" + e.week).includes(qq)
      );
    });
  }, [q, tag, shaders]);

  return (
    <div className="app">
      <div className="grain" aria-hidden />
      <MastHead shaders={shaders} />

      <p className="mast__intro">
        A small personal log. One fragment shader a week — written, tweaked, then archived here.
        Click any tile to read the GLSL.
      </p>

      <div style={{ marginBottom: 24 }}>
        <FilterBar
          q={q}
          setQ={setQ}
          tag={tag}
          setTag={setTag}
          tags={tags}
          filtered={filtered.length}
          total={shaders.length}
        />
      </div>

      <main className="grid">
        {loading && <div className="empty">∅ loading…</div>}
        {err && <div className="empty">∅ {err}</div>}
        {!loading && !err && filtered.length === 0 && (
          <div className="empty">∅ nothing matches that. try clearing filters.</div>
        )}
        {!loading &&
          !err &&
          filtered.map((e) => <EntryCard key={e.id} entry={e} onOpen={setOpen} />)}
      </main>

      <footer className="foot">
        <span>shader brew · vol. 01</span>
        <span className="foot__sep">·</span>
        <span>updated weekly · sundays</span>
        <span className="foot__spacer" />
        {ownerMode && (
          <>
            <Link to="/admin">admin</Link>
            <span className="foot__sep">·</span>
          </>
        )}
        <span>built with raw webgl · no libs</span>
      </footer>

      <Detail entry={open} onClose={() => setOpen(null)} />
    </div>
  );
}
